import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Megaphone,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
  Menu,
  X,
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const COLLAPSE_KEY = "admin-nav-collapsed";

const NAV_ITEMS = [
  {
    to: "/admin/dashboard",
    label: "Home",
    testid: "adminnav-home",
    icon: LayoutDashboard,
  },
  {
    to: "/admin/users",
    label: "Users",
    testid: "adminnav-users",
    icon: Users,
    // Also mark active when we're inside /admin/users/<id>.
    match: (p) => p.startsWith("/admin/users"),
  },
  {
    to: "/admin/ads",
    label: "Ads",
    testid: "adminnav-ads",
    icon: Megaphone,
  },
];

/**
 * AdminShell — collapsible left sidebar + top mobile bar wrapping every
 * admin sub-page. Persists collapsed state in localStorage so the admin's
 * preference survives navigation and reloads.
 *
 * Props:
 *   children       page body
 *   adminEmail     optional string shown in the sidebar footer
 *   right          optional react node rendered inline with the top mobile
 *                  bar (e.g. a range picker on the dashboard)
 */
const AdminShell = ({ children, adminEmail, right }) => {
  const nav = useNavigate();
  const loc = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  // Close the mobile drawer whenever we route.
  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  const logout = async () => {
    try {
      await fetch(`${BASE}/api/admin/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      /* ignore */
    }
    nav("/admin", { replace: true });
  };

  const isActive = (item) =>
    item.match ? item.match(loc.pathname) : loc.pathname === item.to;

  const width = collapsed ? 68 : 232;

  const navList = (
    <nav
      className="flex flex-col gap-1 p-2"
      data-testid="adminnav-list"
      aria-label="Admin navigation"
    >
      {NAV_ITEMS.map((item) => {
        const active = isActive(item);
        const Icon = item.icon;
        return (
          <Link
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            aria-current={active ? "page" : undefined}
            title={collapsed ? item.label : undefined}
            className="flex items-center gap-3 rounded-2xl px-3 py-2 pushable"
            style={{
              background: active ? "#FFB400" : "transparent",
              border: active ? "3px solid #2b2b2b" : "3px solid transparent",
              color: "#fff",
              fontFamily: "Fredoka, sans-serif",
              fontWeight: 600,
              minHeight: 44,
              boxShadow: active ? "0 4px 0 #2b2b2b" : "none",
            }}
          >
            <Icon
              size={22}
              strokeWidth={3}
              style={{ color: active ? "#2b2b2b" : "#fff", flexShrink: 0 }}
              aria-hidden="true"
            />
            {!collapsed ? (
              <span
                className="truncate"
                style={{
                  color: active ? "#2b2b2b" : "#fff",
                }}
              >
                {item.label}
              </span>
            ) : null}
          </Link>
        );
      })}
      <button
        type="button"
        onClick={logout}
        data-testid="adminnav-logout"
        title={collapsed ? "Sign out" : undefined}
        className="flex items-center gap-3 rounded-2xl px-3 py-2 pushable mt-2"
        style={{
          background: "transparent",
          border: "3px solid transparent",
          color: "#FFD5D5",
          fontFamily: "Fredoka, sans-serif",
          fontWeight: 600,
          minHeight: 44,
        }}
      >
        <LogOut size={22} strokeWidth={3} style={{ flexShrink: 0 }} aria-hidden="true" />
        {!collapsed ? <span className="truncate">Sign out</span> : null}
      </button>
    </nav>
  );

  return (
    <div
      className="min-h-screen"
      style={{ background: "#FFF9E6" }}
      data-testid="admin-shell"
    >
      {/* Desktop sidebar — always visible ≥md */}
      <aside
        className="fixed top-0 left-0 h-screen z-40 hidden md:flex flex-col"
        data-testid="adminnav-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
        style={{
          width,
          background: "#5F27CD",
          borderRight: "4px solid #2b2b2b",
          transition: "width 180ms ease",
        }}
      >
        <div
          className="flex items-center justify-between px-3 py-3"
          style={{ borderBottom: "3px solid rgba(0,0,0,0.25)" }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xl" aria-hidden="true">🛡️</span>
            {!collapsed ? (
              <span
                className="font-display text-base truncate"
                style={{ color: "#fff" }}
              >
                67Cafe Admin
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            data-testid="adminnav-toggle"
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            aria-expanded={!collapsed}
            className="pushable rounded-full"
            style={{
              width: 32, height: 32,
              background: "#FFB400",
              border: "3px solid #2b2b2b",
              color: "#2b2b2b",
              display: "inline-flex",
              alignItems: "center", justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 3px 0 #2b2b2b",
            }}
          >
            {collapsed ? (
              <ChevronsRight size={16} strokeWidth={3} />
            ) : (
              <ChevronsLeft size={16} strokeWidth={3} />
            )}
          </button>
        </div>
        {navList}
        <div className="mt-auto p-3">
          {!collapsed && adminEmail ? (
            <div
              className="text-xs truncate opacity-80"
              data-testid="adminnav-email"
              title={adminEmail}
              style={{ color: "#fff" }}
            >
              {adminEmail}
            </div>
          ) : null}
        </div>
      </aside>

      {/* Mobile top bar — sticky, hosts the hamburger + optional `right` node */}
      <header
        className="sticky top-0 z-30 md:hidden border-b-4"
        style={{ background: "#5F27CD", borderColor: "#2b2b2b" }}
      >
        <div className="flex items-center gap-2 px-3 py-2">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            data-testid="adminnav-mobile-open"
            aria-label="Open navigation"
            className="pushable rounded-full"
            style={{
              width: 36, height: 36,
              background: "#FFB400",
              border: "3px solid #2b2b2b",
              boxShadow: "0 3px 0 #2b2b2b",
              display: "inline-flex",
              alignItems: "center", justifyContent: "center",
              color: "#2b2b2b",
              flexShrink: 0,
            }}
          >
            <Menu size={18} strokeWidth={3} />
          </button>
          <span className="text-xl" aria-hidden="true">🛡️</span>
          <span
            className="font-display text-base truncate"
            style={{ color: "#fff" }}
          >
            67Cafe Admin
          </span>
          <div className="ml-auto flex items-center gap-2">{right}</div>
        </div>
      </header>

      {/* Mobile drawer — overlay */}
      {mobileOpen ? (
        <>
          <div
            className="fixed inset-0 z-40 md:hidden"
            style={{ background: "rgba(0,0,0,0.55)" }}
            onClick={() => setMobileOpen(false)}
            data-testid="adminnav-mobile-scrim"
            aria-hidden="true"
          />
          <aside
            className="fixed top-0 left-0 h-screen z-50 md:hidden flex flex-col"
            data-testid="adminnav-mobile-drawer"
            style={{
              width: 260,
              background: "#5F27CD",
              borderRight: "4px solid #2b2b2b",
            }}
          >
            <div
              className="flex items-center justify-between px-3 py-3"
              style={{ borderBottom: "3px solid rgba(0,0,0,0.25)" }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xl" aria-hidden="true">🛡️</span>
                <span
                  className="font-display text-base truncate"
                  style={{ color: "#fff" }}
                >
                  67Cafe Admin
                </span>
              </div>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                data-testid="adminnav-mobile-close"
                aria-label="Close navigation"
                className="pushable rounded-full"
                style={{
                  width: 32, height: 32,
                  background: "#FFB400",
                  border: "3px solid #2b2b2b",
                  boxShadow: "0 3px 0 #2b2b2b",
                  color: "#2b2b2b",
                  display: "inline-flex",
                  alignItems: "center", justifyContent: "center",
                }}
              >
                <X size={16} strokeWidth={3} />
              </button>
            </div>
            {/* Force expanded labels inside the mobile drawer */}
            <MobileNavList onLogout={logout} isActive={isActive} />
            {adminEmail ? (
              <div
                className="mt-auto p-3 text-xs truncate opacity-80"
                style={{ color: "#fff" }}
                title={adminEmail}
              >
                {adminEmail}
              </div>
            ) : null}
          </aside>
        </>
      ) : null}

      {/* Main content — shifted by the sidebar width on desktop */}
      <main
        className="min-h-screen"
        style={{ paddingLeft: 0 }}
        data-testid="admin-shell-main"
      >
        <div
          style={{ paddingLeft: 0 }}
          className={collapsed ? "md:pl-[68px]" : "md:pl-[232px]"}
        >
          {children}
        </div>
      </main>
    </div>
  );
};

// Same links as the desktop navList but with labels always shown — used
// inside the mobile drawer where there is no collapsed state.
const MobileNavList = ({ onLogout, isActive }) => (
  <nav className="flex flex-col gap-1 p-2" aria-label="Admin navigation (mobile)">
    {NAV_ITEMS.map((item) => {
      const active = isActive(item);
      const Icon = item.icon;
      return (
        <Link
          key={item.to}
          to={item.to}
          data-testid={`${item.testid}-mobile`}
          aria-current={active ? "page" : undefined}
          className="flex items-center gap-3 rounded-2xl px-3 py-2 pushable"
          style={{
            background: active ? "#FFB400" : "transparent",
            border: active ? "3px solid #2b2b2b" : "3px solid transparent",
            color: active ? "#2b2b2b" : "#fff",
            fontFamily: "Fredoka, sans-serif",
            fontWeight: 600,
            minHeight: 44,
            boxShadow: active ? "0 4px 0 #2b2b2b" : "none",
          }}
        >
          <Icon
            size={22}
            strokeWidth={3}
            style={{ color: active ? "#2b2b2b" : "#fff", flexShrink: 0 }}
            aria-hidden="true"
          />
          <span className="truncate">{item.label}</span>
        </Link>
      );
    })}
    <button
      type="button"
      onClick={onLogout}
      data-testid="adminnav-logout-mobile"
      className="flex items-center gap-3 rounded-2xl px-3 py-2 pushable mt-2"
      style={{
        background: "transparent",
        border: "3px solid transparent",
        color: "#FFD5D5",
        fontFamily: "Fredoka, sans-serif",
        fontWeight: 600,
        minHeight: 44,
      }}
    >
      <LogOut size={22} strokeWidth={3} style={{ flexShrink: 0 }} aria-hidden="true" />
      <span className="truncate">Sign out</span>
    </button>
  </nav>
);

export default AdminShell;
